<template>
  <div class="base-preview-root">
    <div v-if="url" class="base-preview-toolbar">
      <div v-if="previewMode === 'image'" class="base-preview-toolbar__zoom">
        <Button
          size="small"
          ghost
          icon="md-remove"
          :disabled="imageScale <= minImageScale"
          @click="zoomOut"
        />
        <span class="base-preview-toolbar__scale">{{ scalePercent }}%</span>
        <Button
          size="small"
          ghost
          icon="md-add"
          :disabled="imageScale >= maxImageScale"
          @click="zoomIn"
        />
        <Button size="small" ghost @click="resetImageView">适应</Button>
      </div>
      <div v-else class="base-preview-toolbar__spacer" />
      <Button size="small" type="primary" ghost icon="md-download" @click="handleDownload">
        下载
      </Button>
    </div>
    <div
      v-if="previewMode === 'image'"
      ref="imageViewport"
      class="base-preview base-preview--image"
      @wheel.prevent="onImageWheel"
      @dblclick="resetImageView"
    >
      <div
        class="base-preview__image-wrap"
        :class="{ 'is-panning': panning, 'is-zoomed': imageScale > 1 }"
        :style="imageTransformStyle"
        @mousedown.prevent="onImagePanStart"
      >
        <img
          :src="url"
          class="base-preview__image"
          alt="预览"
          draggable="false"
          @click="handleImageClick"
        />
      </div>
    </div>
    <div v-else class="base-preview" :class="`base-preview--${previewMode}`">
      <iframe
        v-if="previewMode === 'iframe'"
        :src="iframeSrc"
        class="base-preview__iframe"
        frameborder="0"
        allowfullscreen
      />
      <div v-else class="base-preview__fallback">
        该文件格式 {{ fileExt || '未知' }} 无法预览，请点击上方下载按钮
      </div>
    </div>
  </div>
</template>

<script>
const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico'];
const PDF_EXTS = ['pdf'];
const OFFICE_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
const MIN_IMAGE_SCALE = 0.25;
const MAX_IMAGE_SCALE = 5;
const IMAGE_SCALE_STEP = 0.25;

export default {
  props: {
    url: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      imageScale: 1,
      imageTranslateX: 0,
      imageTranslateY: 0,
      panning: false,
      panStartX: 0,
      panStartY: 0,
      panOriginX: 0,
      panOriginY: 0,
      imageClickMoved: false,
      minImageScale: MIN_IMAGE_SCALE,
      maxImageScale: MAX_IMAGE_SCALE,
    };
  },
  computed: {
    fileExt() {
      return this.getFileExt(this.url);
    },
    downloadName() {
      if (this.name) {
        return this.name;
      }
      if (this.url && this.$util && typeof this.$util.parseFileURL === 'function') {
        const parsed = this.$util.parseFileURL(this.url);
        if (parsed && parsed.name) {
          return parsed.name;
        }
      }
      return '文件';
    },
    previewMode() {
      if (!this.url) {
        return 'unsupported';
      }
      if (IMAGE_EXTS.includes(this.fileExt)) {
        return 'image';
      }
      if (PDF_EXTS.includes(this.fileExt) || OFFICE_EXTS.includes(this.fileExt)) {
        return 'iframe';
      }
      return 'unsupported';
    },
    scalePercent() {
      return Math.round(this.imageScale * 100);
    },
    imageTransformStyle() {
      return {
        transform: `translate(${this.imageTranslateX}px, ${this.imageTranslateY}px) scale(${this.imageScale})`,
      };
    },
    iframeSrc() {
      if (!this.url) {
        return '';
      }
      if (OFFICE_EXTS.includes(this.fileExt)) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(this.url)}`;
      }
      if (PDF_EXTS.includes(this.fileExt)) {
        const base = String(this.url).split('#')[0];
        return `${base}#toolbar=0&navpanes=0&view=FitH&zoom=page-width`;
      }
      return this.url;
    },
  },
  watch: {
    url() {
      this.resetImageView();
    },
  },
  beforeDestroy() {
    this.detachPanListeners();
  },
  methods: {
    getFileExt(url) {
      if (!url) {
        return '';
      }
      const path = String(url).split('?')[0].split('#')[0];
      const dot = path.lastIndexOf('.');
      if (dot === -1) {
        return '';
      }
      return path.substring(dot + 1).toLowerCase();
    },
    handleDownload() {
      if (!this.url) {
        return;
      }
      this.DownloadWithName(this.url, this.downloadName);
    },
    resetImageView() {
      this.imageScale = 1;
      this.imageTranslateX = 0;
      this.imageTranslateY = 0;
      this.panning = false;
      this.imageClickMoved = false;
    },
    clampImageScale(scale) {
      return Math.max(MIN_IMAGE_SCALE, Math.min(MAX_IMAGE_SCALE, scale));
    },
    setImageScale(scale) {
      this.imageScale = this.clampImageScale(scale);
      if (this.imageScale <= 1) {
        this.imageTranslateX = 0;
        this.imageTranslateY = 0;
      }
    },
    zoomIn() {
      this.setImageScale(this.imageScale + IMAGE_SCALE_STEP);
    },
    zoomOut() {
      this.setImageScale(this.imageScale - IMAGE_SCALE_STEP);
    },
    handleImageClick() {
      if (this.imageClickMoved) {
        return;
      }
      if (this.imageScale >= 2) {
        this.zoomOut();
      } else {
        this.zoomIn();
      }
    },
    onImageWheel(e) {
      const delta = e.deltaY > 0 ? -IMAGE_SCALE_STEP : IMAGE_SCALE_STEP;
      this.setImageScale(this.imageScale + delta);
    },
    onImagePanStart(e) {
      if (this.imageScale <= 1) {
        return;
      }
      this.panning = true;
      this.imageClickMoved = false;
      this.panStartX = e.clientX;
      this.panStartY = e.clientY;
      this.panOriginX = this.imageTranslateX;
      this.panOriginY = this.imageTranslateY;
      document.addEventListener('mousemove', this.onImagePanMove);
      document.addEventListener('mouseup', this.onImagePanEnd);
    },
    onImagePanMove(e) {
      const dx = e.clientX - this.panStartX;
      const dy = e.clientY - this.panStartY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.imageClickMoved = true;
      }
      this.imageTranslateX = this.panOriginX + dx;
      this.imageTranslateY = this.panOriginY + dy;
    },
    onImagePanEnd() {
      this.panning = false;
      this.detachPanListeners();
    },
    detachPanListeners() {
      document.removeEventListener('mousemove', this.onImagePanMove);
      document.removeEventListener('mouseup', this.onImagePanEnd);
    },
  },
};
</script>

<style lang="less" scoped>
.base-preview-root {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 240px;
  box-sizing: border-box;
}

.base-preview-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border, #e8eaec);
  background: var(--bg2, #fff);
}

.base-preview-toolbar__zoom {
  display: flex;
  align-items: center;
  gap: 8px;
}

.base-preview-toolbar__scale {
  min-width: 48px;
  text-align: center;
  font-size: 12px;
  color: var(--subtext2, #808695);
}

.base-preview-toolbar__spacer {
  flex: 1;
}

.base-preview {
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
  background: var(--bg2, #f5f7fa);
}

.base-preview--image {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 12px;
  user-select: none;
}

.base-preview__image-wrap {
  transition: transform 0.15s ease;
  will-change: transform;
  cursor: zoom-in;

  &.is-zoomed:not(.is-panning) {
    cursor: grab;
  }

  &.is-panning {
    transition: none;
    cursor: grabbing;
  }
}

.base-preview__image {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 180px);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  pointer-events: auto;
}

.base-preview--iframe {
  overflow: hidden;
  padding: 0;
  background: #525659;
}

.base-preview__iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  background: #fff;
}

.base-preview--unsupported {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.base-preview__fallback {
  color: var(--subtext2, #808695);
  font-size: 14px;
  line-height: 22px;
  text-align: center;
}
</style>
